import type { StorageKey, StorageService } from './types';

/**
 * `localStorage`, wrapped so that it cannot take the application down (ADR-0015, ADR-0023).
 *
 * THREE THINGS THIS FILE EXISTS FOR, in order of how badly they bite:
 *
 * 1. **Safari private mode throws on READ, not only on write.** The reflex is
 *    `try { setItem } catch {}` and a bare `getItem` — which is the one that runs at boot,
 *    at module scope, before any error boundary is mounted. The result is a white screen.
 *    Every single access below is inside its own `try`, including the one that merely
 *    reaches for the `localStorage` property (a sandboxed iframe throws on the property
 *    access itself, before any method is called).
 *
 * 2. **A stored value is untrusted input.** It survives upgrades, it is editable in
 *    devtools, and it may have been written by an older build with a different shape.
 *    `get` therefore takes a `parse` that must return `null` for anything it does not
 *    recognise, and a `fallback` used for "absent", "unreadable" and "not what we expected"
 *    alike. Boot cannot be broken by a hand-edited string.
 *
 * 3. **The namespace is the whole key list.** `StorageKey` is a two-member union, and it is
 *    exhaustive on purpose: NO USER DOCUMENT IS EVER PERSISTED (ADR-0023). Adding a third
 *    key is a type change that shows up in review, not a string typed at a call site.
 *
 * The stored encoding is JSON, and it is shared with the inline theme bootstrap in
 * `index.html` — see `theme.ts` for that contract.
 */

/** The three methods of `Storage` this application uses. */
export interface StorageArea {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StorageDeps {
  /** Injected in tests. Defaults to `globalThis.localStorage`, reached defensively. */
  readonly area?: StorageArea | null;
}

/**
 * `localStorage` if it is reachable, `null` if the property access itself throws.
 *
 * Not cached in a module constant: a module-scope read is exactly the boot-time crash this
 * file is about, and it would also freeze the decision before a test can inject anything.
 */
function ambientArea(): StorageArea | null {
  try {
    const area = (globalThis as { localStorage?: StorageArea | null }).localStorage;
    return area ?? null;
  } catch {
    return null;
  }
}

/**
 * Decode one raw string.
 *
 * JSON first, and the bare string as the fallback — the same two-step the inline bootstrap
 * performs, so a value written by hand in devtools (`jsonfmt.theme` = `dark`, no quotes)
 * reads identically in both places instead of theming the first paint and nothing else.
 */
function decode(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export function createStorageService(deps: StorageDeps = {}): StorageService {
  const area = deps.area !== undefined ? deps.area : ambientArea();

  return {
    get<T>(key: StorageKey, parse: (raw: unknown) => T | null, fallback: T): T {
      if (area === null) return fallback;

      let raw: string | null;
      try {
        raw = area.getItem(key);
      } catch {
        // Safari private mode, a sandboxed iframe, a storage quota in a strange state.
        return fallback;
      }
      if (raw === null) return fallback;

      try {
        const parsed = parse(decode(raw));
        return parsed === null ? fallback : parsed;
      } catch {
        // `parse` is caller-supplied and is not trusted to be total either.
        return fallback;
      }
    },

    set(key: StorageKey, value: unknown): void {
      if (area === null) return;
      try {
        const encoded = JSON.stringify(value);
        // `JSON.stringify(undefined)` is `undefined`, and writing the four letters "undefined"
        // would then read back as a value. Absent means absent.
        if (encoded === undefined) {
          area.removeItem(key);
          return;
        }
        area.setItem(key, encoded);
      } catch {
        // Quota exceeded, private mode, a value with a cycle. Persisting a preference is a
        // convenience; failing to persist it is not worth a single pixel of UI.
      }
    },

    remove(key: StorageKey): void {
      if (area === null) return;
      try {
        area.removeItem(key);
      } catch {
        /* see `set` */
      }
    },
  };
}

/**
 * An in-memory `StorageArea`. The second implementation ADR-0015 promised.
 *
 * Used by `createNullServices()` so a command test persists a preference, reads it back and
 * touches no DOM at all — which is what keeps those tests in the bare-Node `logic` project
 * instead of jsdom (ADR-0019).
 */
export function createMemoryStorageArea(initial?: Readonly<Record<string, string>>): StorageArea {
  const cells = new Map<string, string>(Object.entries(initial ?? {}));
  return {
    getItem: (key) => cells.get(key) ?? null,
    setItem: (key, value) => void cells.set(key, value),
    removeItem: (key) => void cells.delete(key),
  };
}

/** `createStorageService` over a fresh in-memory area. */
export function createMemoryStorageService(
  initial?: Readonly<Record<string, string>>,
): StorageService {
  return createStorageService({ area: createMemoryStorageArea(initial) });
}
